<template>
  <div>
    <div :class="s.wrap">
      <div v-if="flag.loading.getContext">
        読み込み中です...
      </div>
      <div v-else-if="flag.saved">
        <p class="text-center fs-xl">お疲れ様でした！</p>
      </div>
      <div v-else-if="definitions.length">
        <div
          v-for="def in definitions" :key="def.id"
          :class="s.field">
          <div class="d-flex align-items-center">
            <p class="fw-bold">{{ def.label }}</p>
            <span v-if="isRequired(def)" :class="s.required_badge">必須</span>
          </div>
          <div :class="s.type">
            <input
              v-if="def.type === 'number'"
              :class="s.input"
              type="text"
              inputmode="decimal"
              v-model="inputs[def.id]">
            <input
              v-else
              :class="s.input"
              type="text"
              v-model="inputs[def.id]">
            <span v-if="def.unit" :class="s.unit">{{ def.unit }}</span>
          </div>
        </div>

        <div v-if="missingRequiredLabels.length" class="text-danger mt-3">
          {{ missingRequiredLabels.join('、') }}を入力してください。
        </div>

        <div class="mt-4 pt-4 bdr-t bdr-bold">
          <div class="d-flex align-items-center">
            <div class="mx-auto">
              <large-btn
                :style="{
                  height: '100px',
                  width: '200px',
                }"
                :disabled="flag.loading.save || !readySubmit"
                @click="save">保存</large-btn>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        入力できる付加データがありません。
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import LargeBtn from '../../parts/LargeBtn.vue';

/**
 * dispatch 付加データ（タクシーメーター走行距離等）の入力モーダル。
 * 入力項目は area の定義（dispatchAdditionalDefinitions）で決まる。
 * data: {
 *   dispatchId: number,
 *   showThanksMessage?: boolean, // 保存後に「お疲れ様でした！」を表示して閉じる（実績確定からの自動遷移用）
 * }
 */
export default {
  name: 'DispatchAdditionals',
  props: ['data'],
  components: {
    LargeBtn,
  },
  data() {
    return {
      flag: {
        loading: {
          getContext: false,
          save: false,
        },
        saved: false,
      },
      definitions: [],
      inputs: {},
    };
  },
  created() {
    if (this.data.dispatchId) {
      this.getContext();
    } else {
      alert('処理中にエラーが発生しました。');
      this.$store.dispatch('modal/contents/hideModal', null, { root: true });
    }
  },
  computed: {
    ...mapState(['user']),
    readySubmit() {
      // 数値項目に数値以外が入っていたら保存不可
      const invalidNumber = this.definitions.some((def) => {
        if (def.type !== 'number') return false;
        const value = this.inputs[def.id];
        if (value === '' || value == null) return false;
        return Number.isNaN(Number(value));
      });
      // 必須項目が未入力なら保存不可（backend でも同じ検証をする）
      return !invalidNumber && !this.missingRequiredLabels.length;
    },
    /** 未入力の必須項目の表示名リスト */
    missingRequiredLabels() {
      return this.definitions
        .filter((def) => this.isRequired(def))
        .filter((def) => {
          const value = this.inputs[def.id];
          return value === '' || value == null;
        })
        .map((def) => def.label);
    },
  },
  methods: {
    isRequired(def) {
      return Number(def.is_required) === 1;
    },
    showLoading() {
      this.$store.dispatch('modal/loadings/showModal', { modalName: 'modalLoadingBallScaleRippleMultiple' }, { root: true });
    },
    hideLoading() {
      this.$store.dispatch('modal/loadings/hideModal', null, { root: true });
    },

    getContext() {
      this.flag.loading.getContext = true;
      this.showLoading();
      this.axios({
        method: 'GET',
        url: '/v1/dispatch/get/detail',
        params: {
          id: this.data.dispatchId,
          withSchedule: 1,
          withSlot: 1,
          withAdditionals: 1,
        },
      })
        .then((response) => {
          const dispatch = response.data.dispatch || null;
          const area = dispatch?.schedule?.area || dispatch?.slot?.area || null;
          this.definitions = area?.dispatchAdditionalDefinitions || [];

          // 既存値をプリセット
          const additionals = dispatch?.additionals || [];
          const inputs = {};
          this.definitions.forEach((def) => {
            const row = additionals.find((a) => a.definition_id === def.id) || null;
            inputs[def.id] = this.presetValue(def, row);
          });
          this.inputs = inputs;
        })
        .catch((error) => {
          console.log(error);
          alert('エラーが発生しました。再度お試しください。');
          this.$store.dispatch('modal/contents/hideModal', null, { root: true });
        })
        .finally(() => {
          this.flag.loading.getContext = false;
          this.hideLoading();
        });
    },

    /** 既存値の初期表示（DB の decimal は "12400.00" の形で返るため数値化して整形する） */
    presetValue(def, row) {
      if (!row) return '';
      if (def.type === 'number') {
        if (row.value_number == null) return '';
        const num = Number(row.value_number);
        return Number.isNaN(num) ? '' : String(num);
      }
      return row.value_string ?? '';
    },

    save() {
      if (!this.readySubmit) return;
      this.flag.loading.save = true;
      this.showLoading();

      const values = this.definitions.map((def) => {
        const raw = this.inputs[def.id];
        let value = raw === '' || raw == null ? null : raw;
        if (value !== null && def.type === 'number') value = Number(value);
        return { definition_id: def.id, value };
      });

      this.axios({
        method: 'POST',
        url: '/v1/dispatch/update/additionalData',
        data: {
          user_id: this.user.id,
          dispatch_id: this.data.dispatchId,
          values,
        },
      })
        .then(() => {
          this.$store.dispatch('dispatch/getDispatches', { hideLoading: true });
          this.hideLoading();
          if (this.data.showThanksMessage) {
            this.flag.saved = true;
            setTimeout(() => this.$store.dispatch('modal/contents/hideModal', null, { root: true }), 3000);
          } else {
            this.$store.dispatch('modal/contents/hideModal', null, { root: true });
          }
        })
        .catch((error) => {
          const message = error.response?.data?.message;
          console.log(error);
          alert(message || 'エラーが発生しました。再度お試しください。');
          this.hideLoading();
        })
        .finally(() => {
          this.flag.loading.save = false;
        });
    },
  },
};
</script>

<style lang="scss" module="s">
.wrap {
  padding: 10px 15px;
}
.required_badge {
  margin-left: 8px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: bold;
  color: #ffffff;
  background-color: var(--bs-danger, #dc3545);
  border-radius: 3px;
}
.field {
  margin-top: 16px;
  &:first-of-type {
    margin-top: 0;
  }
}
.type {
  display: flex;
  align-items: baseline;
  .input {
    width: 95%;
    height: 50px;
    font-size: 24px;
    text-align: right;
    padding-right: 15px;
    margin-top: 8px;
    border: 1px solid var(--bs-gray);
    border-radius: 3px;
  }
  .unit {
    font-size: 18px;
    font-weight: bold;
    margin-left: 10px;
  }
}
</style>
